import { useState, useRef, useCallback, useEffect } from "react";
import { Camera, RefreshCw, CheckCircle, AlertCircle, X } from "lucide-react";

export function CameraCapture({
  onCapture,
  onError,
  label = "Capture Image",
  capturedImage = null,
  className = ""
}) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasPermission, setHasPermission] = useState(null);
  const [error, setError] = useState(null);
  const [isCaptured, setIsCaptured] = useState(!!capturedImage);
  const [preview, setPreview] = useState(capturedImage);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Start camera stream
  const startCamera = useCallback(async () => {
    setError(null);
    
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error("Camera not supported in this browser. Please use a modern browser.");
      }

      // Request camera access with constraints optimized for document capture
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "environment", // Prefer back camera on mobile/tablet
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 }
        },
        audio: false
      });

      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsStreaming(true);
        setHasPermission(true);
        setIsCaptured(false);
        setPreview(null);
      }
    } catch (err) {
      console.error("Camera error:", err);
      setHasPermission(false);
      
      let errorMessage = "Unable to access camera. ";
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        errorMessage += "Please grant camera permission and try again.";
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        errorMessage += "No camera found on this device.";
      } else if (err.name === "NotReadableError" || err.name === "TrackStartError") {
        errorMessage += "Camera is being used by another application.";
      } else {
        errorMessage += err.message || "Unknown error occurred.";
      }
      
      setError(errorMessage);
      onError?.(errorMessage);
    }
  }, [onError]);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
  }, []);

  // Capture image from video stream
  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Get image as base64 (JPEG for smaller size)
    const imageDataUrl = canvas.toDataURL("image/jpeg", 0.9);
    const base64Data = imageDataUrl.split(",")[1];

    // Update state
    setPreview(imageDataUrl);
    setIsCaptured(true);
    stopCamera();

    // Callback with base64 data
    onCapture?.(base64Data, imageDataUrl);
  }, [onCapture, stopCamera]);

  // Retake photo
  const retake = useCallback(() => {
    setPreview(null);
    setIsCaptured(false);
    onCapture?.(null, null);
    startCamera();
  }, [startCamera, onCapture]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  // Auto-start camera if no captured image
  useEffect(() => {
    if (!capturedImage && !isStreaming && !isCaptured) {
      startCamera();
    }
  }, [capturedImage, isStreaming, isCaptured, startCamera]);

  return (
    <div className={`camera-capture ${className}`} data-testid="camera-capture">
      {/* Hidden canvas for image capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Camera view or preview */}
      <div className="relative aspect-[4/3] bg-slate-900 rounded-2xl overflow-hidden">
        {/* Error state */}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center bg-red-50">
            <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
            <p className="text-red-700 font-medium mb-4">{error}</p>
            <button
              onClick={startCamera}
              className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors flex items-center gap-2"
              data-testid="retry-camera"
            >
              <RefreshCw className="w-5 h-5" />
              Try Again
            </button>
          </div>
        )}

        {/* Captured preview */}
        {isCaptured && preview && !error && (
          <div className="relative w-full h-full">
            <img 
              src={preview} 
              alt="Captured" 
              className="w-full h-full object-contain"
              data-testid="captured-preview"
            />
            <div className="absolute top-3 right-3 bg-green-500 text-white p-2 rounded-full shadow-lg">
              <CheckCircle className="w-6 h-6" />
            </div>
          </div>
        )}

        {/* Live video stream */}
        {!isCaptured && !error && (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              data-testid="camera-video"
            />
            
            {/* Camera guide overlay */}
            {isStreaming && (
              <div className="absolute inset-0 pointer-events-none">
                {/* Corner guides */}
                <div className="absolute top-4 left-4 w-12 h-12 border-l-4 border-t-4 border-white/70 rounded-tl-lg" />
                <div className="absolute top-4 right-4 w-12 h-12 border-r-4 border-t-4 border-white/70 rounded-tr-lg" />
                <div className="absolute bottom-4 left-4 w-12 h-12 border-l-4 border-b-4 border-white/70 rounded-bl-lg" />
                <div className="absolute bottom-4 right-4 w-12 h-12 border-r-4 border-b-4 border-white/70 rounded-br-lg" />
                
                {/* Center guide text */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black/40 backdrop-blur-sm px-4 py-2 rounded-lg">
                    <p className="text-white text-sm font-medium">Position badge within frame</p>
                  </div>
                </div>
              </div>
            )}

            {/* Loading state */}
            {!isStreaming && !error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-800">
                <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mb-4" />
                <p className="text-white/80">Starting camera...</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex justify-center gap-4 mt-4">
        {!isCaptured && isStreaming && (
          <button
            onClick={captureImage}
            className="px-8 py-4 bg-gradient-to-r from-blue-600 to-green-500 text-white text-xl font-semibold rounded-2xl hover:shadow-lg transition-all flex items-center gap-3"
            data-testid="capture-button"
          >
            <Camera className="w-6 h-6" />
            {label}
          </button>
        )}

        {isCaptured && (
          <button
            onClick={retake}
            className="px-8 py-4 bg-slate-200 text-slate-700 text-xl font-semibold rounded-2xl hover:bg-slate-300 transition-colors flex items-center gap-3"
            data-testid="retake-button"
          >
            <RefreshCw className="w-6 h-6" />
            Retake Photo
          </button>
        )}
      </div>
    </div>
  );
}

export default CameraCapture;
